import '@mantine/core/styles.css';
import '@mantine/dates/styles.css';
import 'mantine-react-table/styles.css';
import '../ChatOverrides.scss';

import {
    Avatar,
    ChatContainer,
    ConversationHeader,
    InfoButton,
    MainContainer,
    Message,
    MessageInput,
    MessageList,
    MessageSeparator,
    TypingIndicator,
} from '@chatscope/chat-ui-kit-react';
import {
    Box,
    Container,
    Flex,
    Group,
    Loader,
    LoadingOverlay,
    MultiSelect,
    Portal,
    Select,
    Text,
} from '@mantine/core';
import { useDebouncedCallback, useLocalStorage } from '@mantine/hooks';
import { useMemo, useState } from 'react';

import ChatMessageList from './ChatMessageList';
import EmptyMessageList from './EmptyMessageList';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TestMessage from './TestMessage';
import { faEllipsis } from '@fortawesome/pro-solid-svg-icons';
import { getConversationMessages } from '../../../../../http/apiClient';
import useGetConversationByIdQuery from '../../../../../http/queries/engagement/useGetConversationById';
import { usePaginatedQuery } from '../../../../../http/queries/usePaginatedQuery';

const TEST_MESSAGES = [
    {
        userId: 'alice',
        at: '2025-04-17T13:00:00Z',
        text: 'Hey!',
        direction: 'In',
    },
    {
        userId: 'bob',
        at: '2025-04-17T13:01:00Z',
        text: 'Hi Alice',
        direction: 'Out',
    },
    {
        userId: 'bob',
        at: '2025-04-17T13:01:30Z',
        text: 'How are you?',
        direction: 'Out',
    },
    {
        userId: 'alice',
        at: '2025-04-17T13:02:00Z',
        text: 'Good, thanks!',
        direction: 'In',
    },
    {
        userId: 'alice',
        at: '2025-04-17T13:03:00Z',
        text: 'You?',
        direction: 'In',
    },
    {
        userId: 'alice',
        at: '2025-04-17T13:00:00Z',
        text: 'Hey!',
        direction: 'In',
    },
    {
        userId: 'bob',
        at: '2025-04-17T13:01:00Z',
        text: 'Hi Alice',
        direction: 'Out',
    },
    {
        userId: 'bob',
        at: '2025-04-17T13:01:30Z',
        text: 'How are you?',
        direction: 'Out',
    },
    {
        userId: 'alice',
        at: '2025-04-17T13:02:00Z',
        text: 'Good, thanks!',
        direction: 'In',
    },
    {
        userId: 'alice',
        at: '2025-04-17T13:03:00Z',
        text: 'You?',
        direction: 'In',
    },
    {
        userId: 'alice',
        at: '2025-04-17T13:00:00Z',
        text: 'Hey!',
        direction: 'In',
    },
    {
        userId: 'bob',
        at: '2025-04-17T13:01:00Z',
        text: 'Hi Alice',
        direction: 'Out',
    },
    {
        userId: 'bob',
        at: '2025-04-17T13:01:30Z',
        text: 'How are you?',
        direction: 'Out',
    },
    {
        userId: 'alice',
        at: '2025-04-17T13:02:00Z',
        text: 'Good, thanks!',
        direction: 'In',
    },
    {
        userId: 'alice',
        at: '2025-04-17T13:03:00Z',
        text: 'You?',
        direction: 'In',
    },
];

/**
 * @param {Array<{userId: string, at: string|Date, [key: string]: any}>} messages
 * @returns {Array<Array<Object>>}
 */
function sortAndGroupMessages(messages) {
    // 1) Clone & sort by timestamp ascending
    const sorted = [...messages].sort(
        (a, b) => new Date(a.at).getTime() - new Date(b.at).getTime()
    );

    // 2) Iterate and group consecutive messages by userId
    const groups = [];
    for (const msg of sorted) {
        const lastGroup = groups[groups.length - 1];
        if (!lastGroup || lastGroup[0].userId !== msg.userId) {
            // new group for a different user
            groups.push([msg]);
        } else {
            // same user as previous message → append to current group
            lastGroup.push(msg);
        }
    }
    return groups;
}

const ConversationTypingIndicator = ({
    userTyping = false,
    botTyping = false,
}) => {
    return (
        <Flex
            justify={'space-between'}
            px={'lg'}
            py={0}
            mt={'auto'}>
            <Group
                gap={'xs'}
                py={0}
                my={0}>
                {userTyping ? (
                    <>
                        <Text>User is typing</Text>
                        <FontAwesomeIcon
                            icon={faEllipsis}
                            beatFade
                        />
                    </>
                ) : null}
            </Group>
            <Group
                gap={'xs'}
                py={0}
                my={0}>
                {botTyping ? (
                    <>
                        <Text>Reply is pending</Text>
                        <FontAwesomeIcon
                            icon={faEllipsis}
                            beatFade
                        />
                    </>
                ) : null}
            </Group>
        </Flex>
    );
};

const ConversationContainer = ({ conversationId }) => {
    //const messages = TEST_MESSAGES;
    const userTyping = false;
    const botTyping = false;

    const { data: conversation, isLoading: conversationLoading } =
        useGetConversationByIdQuery(conversationId);

    const {
        isLoading: messagesLoading,
        isError,
        error,
        data: paginatedData,
    } = usePaginatedQuery({
        queryFn: (queryParams) =>
            getConversationMessages({ id: conversationId, queryParams }),
        queryKey: ['chat_messages', conversationId],
        refetchInterval: 15_0000,
        globalFilter: null,
        pagination: {},
        sorting: {},
    });

    console.log(paginatedData);
    const { data: messages = [], pagination: dataPagination } =
        paginatedData || {
            messages: [],
        };

    return (
        <MainContainer>
            {conversationLoading ? (
                <Loader visible={true} />
            ) : (
                <ChatContainer
                    style={{
                        minHeight: '600px',
                        maxHeight: '600px',
                    }}>
                    <ConversationHeader>
                        <Avatar
                            name={conversation.name}
                            src='https://chatscope.io/storybook/react/assets/emily-xzL8sDL2.svg'
                        />
                        <ConversationHeader.Content
                            info={conversation?.campaign?.name || 'campaign'}
                            userName={conversation.name}
                        />
                        <ConversationHeader.Actions>
                            <InfoButton />
                        </ConversationHeader.Actions>
                    </ConversationHeader>

                    <MessageList
                        loading={messagesLoading}
                        style={{ overflowY: 'scroll', height: '100%' }}
                        loadingMorePosition={'top'}
                        onYReachStart={console.log('load more')}
                        autoScrollToBottom={true}
                        autoScrollToBottomOnMount={true}
                        disableOnYReachWhenNoScroll={true}
                        typingIndicator={
                            <ConversationTypingIndicator
                                userTyping={userTyping}
                                botTyping={botTyping}
                            />
                        }>
                        <MessageList.Content>
                            {messages.length > 0 && !messagesLoading ? (
                                <ChatMessageList messages={messages} />
                            ) : null}

                            {messages.length === 0 && !messagesLoading ? (
                                <EmptyMessageList />
                            ) : null}

                            {conversation.stopped ? (
                                <MessageSeparator
                                    content={'Stopped'}
                                    className='cs-message-separator-error'
                                />
                            ) : null}

                            {conversation.complete ? (
                                <MessageSeparator
                                    content={'Complete'}
                                    className='cs-message-separator-success'
                                />
                            ) : null}
                        </MessageList.Content>
                    </MessageList>

                    <MessageInput
                        attachButton={false}
                        placeholder='Type message here'
                    />
                </ChatContainer>
            )}
        </MainContainer>
    );
};
export default ConversationContainer;
